import { Upload } from 'lucide-react';

import { useFileDrop } from '@/hooks/useFileDrop';

// The file area itself as a drop target.
//
// Not a FileDropZone: that component *is* the target — a dashed box with its own
// copy and a hidden file input behind a click. This wraps content that already
// exists (the grid, the list, an empty folder's line) and has to leave every
// card clickable, so it draws nothing until a drag arrives and never claims the
// click. The Upload button keeps the browse path; this is only the drag one.
//
// The internal file→folder drags are dnd-kit, which works in pointer events —
// an OS file drag fires none of those, and these HTML5 handlers see none of
// dnd-kit's. The two gestures cannot be confused for one another.

interface JobFileDropAreaProps {
  /** The raw dropped files. Type filtering belongs to the caller, as everywhere else. */
  onFiles: (files: File[]) => void;
  /** Where the drop lands, said while it is being aimed rather than after. */
  destination: string;
  /** No drop while the list underneath is loading or broken. */
  disabled?: boolean;
  /** Extra classes on the wrapper — an empty folder needs a height to aim at. */
  className?: string;
  children: React.ReactNode;
}

export function JobFileDropArea({
  onFiles,
  destination,
  disabled = false,
  className = '',
  children,
}: JobFileDropAreaProps) {
  const { isDragOver, dropHandlers } = useFileDrop({ onFiles, disabled });

  return (
    <div
      {...(disabled ? {} : dropHandlers)}
      data-testid="job-file-drop-area"
      className={`relative rounded-xl ${className}`}
    >
      {children}

      {isDragOver && (
        // Over the content rather than in place of it: what is being dropped
        // into stays visible underneath, which is how a file manager behaves.
        // pointer-events-none so the overlay can't swallow the drop it invites.
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-blue-50/80 ring-2 ring-blue-500">
          <p className="flex items-center gap-2 rounded-lg bg-white/90 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm">
            <Upload className="h-4 w-4" aria-hidden="true" />
            Drop to upload to {destination}
          </p>
        </div>
      )}
    </div>
  );
}
